import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  advanceStatus,
  captureDrawList,
  clearSelectedStop,
  updateStopData,
} from '../store/stopsSlice';
import { showToast } from '../store/uiSlice';
import { getEventCoords } from '../services/lastKnownPosition'; // CLAUDE-SYNC: v1.7.20
import { StatusBadge, PriorityBadge, StatusPipeline, ActivityTimeline, resolveSrc } from '../components/index';
import {
  getStatusLabel,
  PHLEBONLY_STATUS_LABEL,
  PHLEBONLY_STRING_TO_NUMERIC,
  STATUS_API_VALUES,
  STATUS_STRING_TO_NUMERIC,
} from '../constants/status';
import { updateRouteStopStatus } from '../services/routeService';
import { updatePhlebNotes, updateCollectedCounts, updateDriverUser, addDropSiteFacilityId } from '../services/stopsService';
import { uploadStopPhoto, getStopPhotos, uploadPhotoNotes } from '../services/photosService';
import CameraModal from '../components/CameraModal';
import ExceptionPanel from './ExceptionPanel';
import { utcTimeToLocal } from '../Utils';
import { reportException } from '../services/exceptionService';
import { getAllDrivers, getAllFacility, getAllPhlebs } from '../services/userService';
import ErrorBoundaryDetail from '../components/ErrorBoundaryDetail';

// ─── Status numeric reference (mirrors STATUS_STRING_TO_NUMERIC) ──────────────
// 0 not_started | 1 arrived | 2 in_progress | 3 ready
// 4 in_transit  | 5 picked_up | 6 delivered | 7 exception
//
// Phleb-only:
// 0 not_started | 1 arrived | 2 in_progress | 3 complete | 4 delivered | 6 in_transit
// ─────────────────────────────────────────────────────────────────────────────

// Labels for the primary advance button (shown when nextActionLabel !== null)
const NEXT_ACTION_LABEL = {
  0: 'Mark Arrived',
  1: 'Mark Halfway',
  // s=2 shows the destination picker instead of a simple button
  // s=4 (in_transit) shows the delivery picker instead of a simple button
};

// Photo strip label per stage
const PHOTO_STEP_LABEL = {
  0: 'Arrival Photo',
  1: 'Progress Photo',
  2: 'Specimens Photo',
  4: 'In-Transit Photo',   // in_transit — standard flow
  6: 'Delivery Photo',     // delivered  — standard flow
};

// Photo type string sent to the upload API per stage
const PHOTO_STEP_TYPE = {
  0: 'arrival',
  1: 'progress',
  2: 'specimen',
  4: 'in_transit',
  6: 'delivery',
};

// API values for the phleb-only advance chain
const PHLEBONLY_API_VALUES = {
  1: 'arrived',
  2: 'in_progress',
  3: 'complete',
  4: 'in_transit',   // phleb-only in_transit
  5: 'delivered',    // phleb-only delivered (numeric 4 in PHLEBONLY_STRING_TO_NUMERIC)
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ url, images, startIndex = 0, onClose }) {
  const gallery = images && images.length > 0 ? images : (url ? [url] : []);
  const [index, setIndex] = useState(Math.min(startIndex, Math.max(gallery.length - 1, 0)));
  const hasMultiple = gallery.length > 1;

  const goPrev = (e) => { e.stopPropagation(); setIndex((i) => (i - 1 + gallery.length) % gallery.length); };
  const goNext = (e) => { e.stopPropagation(); setIndex((i) => (i + 1) % gallery.length); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (hasMultiple && e.key === 'ArrowLeft') setIndex((i) => (i - 1 + gallery.length) % gallery.length);
      if (hasMultiple && e.key === 'ArrowRight') setIndex((i) => (i + 1) % gallery.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, hasMultiple, gallery.length]);

  if (gallery.length === 0) return null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 22, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>

      {hasMultiple && (
        <button onClick={goPrev} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 26, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
      )}

      <img
        src={gallery[index]}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
        alt="Preview"
      />

      {hasMultiple && (
        <button onClick={goNext} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 26, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
      )}

      {hasMultiple && (
        <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: 20 }}>
          {index + 1} / {gallery.length}
        </div>
      )}
    </div>
  );
}

// ─── NoteModal ────────────────────────────────────────────────────────────────
function NoteModal({ open, existingNote, fileKey, onSave, onClose }) {
  const [text, setText] = useState(existingNote || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setText(existingNote || '');
  }, [open, existingNote]);

  if (!open) return null;

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await new Promise((res) => setTimeout(res, 700));
      onSave(text.trim());
    } catch (err) {
      console.error('Note save failed', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 400,
          background: 'white', borderRadius: 16,
          padding: '22px 20px 20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
          animation: 'popIn 0.18s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--rp)' }}>
            {existingNote ? 'Edit Note' : 'Add Note'}
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#F3EEF5', border: 'none', borderRadius: '50%',
              width: 28, height: 28, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: '#8A7A8D',
            }}
          >✕</button>
        </div>

        <textarea
          autoFocus
          rows={4}
          placeholder="Type your note here…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{
            width: '100%', border: '1.5px solid var(--rgray)', borderRadius: 10,
            padding: '10px 12px', fontFamily: 'inherit', fontSize: 13,
            color: '#1a1a1a', resize: 'none', outline: 'none',
            lineHeight: 1.6, boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => (e.target.style.borderColor = 'var(--rp)')}
          onBlur={(e) => (e.target.style.borderColor = 'var(--rgray)')}
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid var(--rgray)',
              background: 'white', fontSize: 13, fontWeight: 600,
              color: '#8A7A8D', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !text.trim()}
            style={{
              flex: 2, padding: '11px', borderRadius: 10, border: 'none',
              background: saving || !text.trim() ? '#ccc' : 'var(--rp)',
              color: saving || !text.trim() ? '#999' : 'white',
              fontSize: 13, fontWeight: 600,
              cursor: saving || !text.trim() ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s',
            }}
          >
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.93); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────────────
function Stepper({ currentStage, value, max, onChange }) {
  // Disable stepper once the stop is complete (ready=3, in_transit=4, picked_up=5, delivered=6)
  const isDisabled = currentStage >= 3;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 0,
      opacity: isDisabled ? 0.4 : 1,
      pointerEvents: isDisabled ? 'none' : 'auto',
    }}>
      <button
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={isDisabled || value <= 0}
        style={{
          width: 34, height: 34, borderRadius: '20px',
          border: '1px solid var(--rgray)',
          background: 'white',
          fontSize: 18, fontWeight: 700,
          cursor: (isDisabled || value <= 0) ? 'not-allowed' : 'pointer',
          color: value <= 0 ? 'var(--rgray)' : 'var(--rp)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1,
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >−</button>

      <div style={{
        width: 40, height: 34,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, fontWeight: 700, color: 'var(--rp)',
      }}>
        {value}
      </div>

      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={isDisabled || value >= max}
        style={{
          width: 34, height: 34, borderRadius: '20px',
          fontSize: 18, fontWeight: 700,
          background: 'var(--rp)',
          cursor: (isDisabled || value >= max) ? 'not-allowed' : 'pointer',
          color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, border: 'none',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >+</button>
    </div>
  );
}

// ─── PhlebDetail ──────────────────────────────────────────────────────────────
function PhlebDetailInner() {
  const dispatch = useDispatch();

  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState('specimen');
  const [cameraTitle, setCameraTitle] = useState('Take Photo');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localCounts, setLocalCounts] = useState({});
  const [savingCounts, setSavingCounts] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [fetchingPhotos, setFetchingPhotos] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [excOpen, setExcOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState(null);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  const [specimenPhotoNotes, setSpecimenPhotoNotes] = useState({}); // { photoIndex: noteText }
  const [specimenPhotoCameraIdx, setSpecimenPhotoCameraIdx] = useState(null); // track which photo being

  // Note modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [specimenNotes, setSpecimenNotes] = useState({});
  const [savedNote, setSavedNote] = useState('');

  // Destination picker state (used at s=2 and s=4)
  const [destSelected, setDestSelected] = useState('');
  const [destPhoto, setDestPhoto] = useState(false);
  const [dropFacility, setDropFacility] = useState('');
  const [facilityData, setFacilityData] = useState([]);
  const [pickupCourier, setPickupCourier] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Couriers
  const [couriers, setCouriers] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [courierError, setCourierError] = useState(false);

  // Supply flags
  const [supplyFlags, setSupplyFlags] = useState({ supplies: null, backup: null });
  const [savingFlags, setSavingFlags] = useState(false);

  const { facilities, stopStates, drawListPhotos, selectedStopId, specimenTypes } = useSelector((s) => s.stops);
  const currentUser = useSelector((state) => state.auth?.user);

  const specimenLabelMap = specimenTypes.reduce((acc, t) => {
    if (t.active !== false) acc[t.id] = t.name;
    return acc;
  }, {});

  const allStops = facilities.flatMap((f) => f.stops || []);
  const stop = allStops.find((s) => s.id === selectedStopId);

  useEffect(() => {
    const getFacility = async () => {
      const filter = { where: {}, sortBy: "name ASC", status: true }
      try {
        const res = await getAllFacility(filter);
        const facilities = res?.data || [];
        const sorted = facilities.sort((a, b) => a.name.localeCompare(b.name));
        setFacilityData(sorted)
      } catch (error) {
        console.log(error);
      }
    };

    getFacility()
  }, [])

  // CLAUDE-SYNC: v1.6 N2 — load both couriers AND phlebotomists for pickup selection (2026-07-17)
  // Karen's use case: she leaves blood at Elgin for another phleb to take to lab.
  // Previously only drivers showed in the pickup list.
  // Now: all couriers + all phlebs + "Another Phlebotomist (TBD)" option
  useEffect(() => {
    let cancelled = false;
    setLoadingCouriers(true);
    setCourierError(false);
    Promise.all([getAllDrivers(), getAllPhlebs()])
      .then(([driversRes, phlebsRes]) => {
        if (!cancelled) {
          const drivers = driversRes?.data ?? [];
          const phlebs = phlebsRes?.data ?? [];
          // Label phlebs so Jasmine can distinguish in the dropdown
          const labelledPhlebs = phlebs.map(p => ({ ...p, _isPhlebPickup: true }));
          // TBD placeholder at top of phleb section
          const tbddriver = { id: 'tbd_driver', name: ' Courier (TBD)', _isTbd: true };
          setCouriers([...drivers, tbddriver, ...labelledPhlebs]);
        }
      })
      .catch(() => { if (!cancelled) setCourierError(true); })
      .finally(() => { if (!cancelled) setLoadingCouriers(false); });
    return () => { cancelled = true; };
  }, []);

  if (!stop) {
    return (
      <div className="detail-panel open">
        <div className="detail-header">
          <button className="back-btn" onClick={() => dispatch(clearSelectedStop())}>← Back</button>
          <div style={{ fontSize: 14, color: 'var(--bodygray)' }}>Stop not found</div>
        </div>
      </div>
    );
  }

  const isPhlebOnly = stop.serviceType === 'phlebotomy_only';

  // ── Resolve current numeric status ───────────────────────────────────────
  // Use store override first; fall back to converting the stop's string status.
  const s = stopStates[stop.id] ??
    (isPhlebOnly
      ? PHLEBONLY_STRING_TO_NUMERIC[stop.status]
      : STATUS_STRING_TO_NUMERIC[stop.status])
    ?? 0;

  // ── Derived booleans ─────────────────────────────────────────────────────
  // Standard flow numerics: in_transit=4, delivered=6
  const isInTransit = !isPhlebOnly && s === 4;
  const isDelivered = !isPhlebOnly && s === 6;
  // Phleb-only: delivered=4
  const phlebDelivered = isPhlebOnly && s === 4;

  // Drop path: phleb went in_transit then dropped at another facility (ready follows in_transit in timeline)
  const isDropPath = (() => {
    const tl = stop.timeline ?? [];
    let seenTransit = false;
    return tl.some(e => {
      if (e.status === 'in_transit') seenTransit = true;
      return seenTransit && e.status === 'ready';
    });
  })();

  // Photo required before confirming drop/lab destination
  const destPhotoRequired = false;

  // Courier is mandatory only when leaving specimens and facility has no assigned driver
  const facilityDefaultDriverId = stop.driverId ?? '';
  const courierMandatory = destSelected === 'leave' && !facilityDefaultDriverId;
  const canConfirmDest = !!destSelected
    && (!destPhotoRequired || destPhoto)
    && (!courierMandatory || !!pickupCourier)
    && (destSelected !== 'drop' || (!!dropFacility && !!pickupCourier));

  const photos = stop.photos ?? [];
  const hasSpecimenPhoto = photos.some((p) => p.photoType === 'specimen');
  const note = stop.phlebNotes ?? stop.notes ?? '';
  const dlCaptured = drawListPhotos[stop.id] || photos.some((p) => p.photoType === 'draw_list' || p.label === 'draw_list');
  const dlPhoto = photos.find((p) => p.photoType === 'draw_list' || p.label === 'Draw List');

  // ── Labels for the current stage ─────────────────────────────────────────
  const nextActionLabel = NEXT_ACTION_LABEL[s] ?? null;
  const photoStepLabel = PHOTO_STEP_LABEL[s] ?? 'Photo';

  // The "ready for pickup" note textarea only shows in standard flow at s=2 → leaving
  const isReadyStep = !isPhlebOnly && s === 2;

  // ── Specimen counts ───────────────────────────────────────────────────────
  const expectedCounts = specimenTypes
    .filter((t) => t.active !== false)
    .map((t) => {
      const raw = stop.expectedCounts ?? stop.specimenCounts;
      let count = 0;
      if (Array.isArray(raw)) {
        const match = raw.find(
          (i) => String(i.id ?? i.specimenTypeId ?? i._id) === String(t.id)
        );
        count = match ? Number(match.count ?? match.expected ?? 0) : 0;
      } else if (raw) {
        count = Number(raw[t.id] ?? raw[String(t.id)] ?? 0);
      }
      return { id: String(t.id), count };
    });

  const collectedCounts = (() => {
    const collected = stop.collectedCounts || {};
    const base = Object.fromEntries(
      specimenTypes.filter((t) => t.active !== false).map((t) => [String(t.id), 0])
    );
    return {
      ...base,
      ...Object.fromEntries(
        Object.entries(collected).map(([k, v]) => [String(k), Number(v)])
      ),
    };
  })();

  const displayCounts = { ...collectedCounts, ...localCounts };

  const totalExpectedCountSum = expectedCounts.reduce((a, b) => a + b?.count, 0);
  const totalCollectedCountSum = expectedCounts.reduce((a, { id }) => a + Number(displayCounts[String(id)] ?? 0), 0);

  // CLAUDE-SYNC: v1.7.12 — "Other" non-collection is now a count (stepper),
  // matching Hard Sticks/Refusals, per Tanaya's Aug 26 call. otherReason
  // stays as an optional note, no longer the only way to record it.
  const [hardSticksAndRefusal, setHardSticksAndRefusal] = useState({
    hardSticks: stop?.hardSticks ?? 0,
    refusals: stop?.refusals ?? 0,
    otherNotCollectedCount: stop?.otherNotCollectedCount ?? 0,
    otherReason: stop?.otherReason ?? '',
  });

  const hasCountChanges = Object.keys(localCounts).length > 0;
  const hasHardStickChanges = Number(hardSticksAndRefusal.hardSticks) !== Number(stop?.hardSticks ?? 0) ||
    Number(hardSticksAndRefusal.refusals) !== Number(stop?.refusals ?? 0) ||
    Number(hardSticksAndRefusal.otherNotCollectedCount) !== Number(stop?.otherNotCollectedCount ?? 0) ||
    (hardSticksAndRefusal.otherReason || '') !== (stop?.otherReason ?? '');

  const hasUnsavedChanges = hasCountChanges || hasHardStickChanges;

  const hardSticksCounts = Number(hardSticksAndRefusal.hardSticks);
  const refusalsCounts = Number(hardSticksAndRefusal.refusals);
  const otherNotCollectedCounts = Number(hardSticksAndRefusal.otherNotCollectedCount);
  const totalAccountedFor = totalCollectedCountSum + hardSticksCounts + refusalsCounts + otherNotCollectedCounts;


  // ── Helpers ───────────────────────────────────────────────────────────────
  const openLightbox = (p) => {
    const src = resolveSrc(p);
    if (src) setLightboxUrl(src);
  };

  const openCamera = (type, title) => {
    setCameraType(type);
    setCameraTitle(title);
    setCameraOpen(true);
  };

  const handleCameraCapture = async (file, previewDataUrl) => {
    setCameraOpen(false);
    setUploadingPhoto(true);
    try {
      const res = await uploadStopPhoto(stop.id, cameraType, file);
      const up = res?.data ?? {};

      const returnedPhotos = up.photos ?? [];
      const lastPhoto = returnedPhotos[returnedPhotos.length - 1];

      const newPhoto = {
        id: lastPhoto?.id ?? `local_${Date.now()}`,
        url: lastPhoto?.url ?? null,
        fileKey: lastPhoto?.fileKey ?? null,
        photoType: lastPhoto?.photoType ?? cameraType,
        label: cameraType === 'draw_list' ? 'Add'
          : cameraType === 'delivery' ? 'Add'
          : cameraType === 'handoff' ? 'Add'
          : photoStepLabel,
        timestamp: up.timestamp ?? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        previewUrl: previewDataUrl,
        notes: null,
      };
      dispatch(updateStopData({ stopId: stop.id, updates: { photos: [...photos, newPhoto] } }));
      if (cameraType === 'draw_list') dispatch(captureDrawList({ stopId: stop.id }));
      if (cameraType === 'handoff' || cameraType === 'delivery') setDestPhoto(true);
      dispatch(showToast(`${newPhoto.label} saved ✓`));
    } catch (err) {
      console.error('Photo upload failed', err);
      dispatch(showToast('Photo upload failed — try again'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Helpers: time from API response ──────────────────────────────────────
  const extractTimestamp = (result) => {
    const rawTs = result?.data?.timestamp || result?.data?.updatedAt;
    const d = rawTs ? new Date(rawTs) : new Date();
    const timestamp = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const timeHHMM = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    return { timestamp, timeHHMM, date: result?.data?.date ?? new Date().toISOString().split('T')[0] };
  };

  // CLAUDE-SYNC: v1.7.12 (2026-09-02) — capture optional GPS coordinates on
  // Arrived/Delivered, mirroring the Clock In/Out location pattern in
  // TopBar.jsx. Best-effort: never blocks the status update if geolocation
  // is denied, unsupported, or times out. Foundation for the future
  // mileage-audit feature (per Tanaya).
  // CLAUDE-SYNC: v1.7.20 — now uses the shared last-known-position helper
  // (instant when the background watch has a recent fix; falls back to the
  // old one-shot lookup). See services/lastKnownPosition.js.
  const getCoordsSafe = () => getEventCoords();

  // ── Simple advance (Mark Arrived / Mark Halfway) ──────────────────────────
  const handleAdvance = async () => {
    if (advancing) return;
    const nextNumeric = s + 1;
    const apiStatus = isPhlebOnly ? PHLEBONLY_API_VALUES[nextNumeric] : STATUS_API_VALUES[nextNumeric];
    if (!apiStatus) return;

    setAdvancing(true);
    try {
      // CLAUDE-SYNC: v1.7.20 — capture coordinates on EVERY status change
      // (arrived, in progress, in transit / "taking it with me", ready /
      // "leaving it here", picked up, delivered), not just arrived/delivered,
      // so the Clock-In Audit export can map the full route (per Tanaya).
      const coords = await getCoordsSafe();
      const result = await updateRouteStopStatus(stop.id, apiStatus, coords?.lat, coords?.lng);
      const { timestamp, timeHHMM } = extractTimestamp(result);

      dispatch(advanceStatus({
        stopId: stop.id, 
        newStatus: nextNumeric,
        timestamp,
        timeMinutes: timeHHMM,
      }));
      dispatch(showToast(`${isPhlebOnly ? PHLEBONLY_STATUS_LABEL[nextNumeric] : getStatusLabel(nextNumeric)} ✓`));
    } catch (err) {
      console.error('Status update failed', err);
      dispatch(showToast('Failed to update status — please try again'));
    } finally {
      setAdvancing(false);
    }
  };

  const handleNoteBlur = async () => {
    if (!noteText.trim() || noteText.trim() === note) return;
    try {
      await updatePhlebNotes(stop.id, noteText.trim());
      dispatch(updateStopData({ stopId: stop.id, updates: { phlebNotes: noteText.trim() } }));
    } catch (err) {
      console.error('Note save failed', err);
    }
  };

  // ── Save collected counts ─────────────────────────────────────────────────

  const handleSaveCounts = async () => {
  if (!hasUnsavedChanges) return;
  setSavingCounts(true);
  try {
    const payload = {
      collectedCounts: Object.fromEntries(
        Object.entries({ ...collectedCounts, ...localCounts }).map(([id, count]) => [String(id), Number(count)])
      ),
      hardSticks: hardSticksAndRefusal.hardSticks,
      refusals: hardSticksAndRefusal.refusals,
      otherNotCollectedCount: hardSticksAndRefusal.otherNotCollectedCount,
      otherReason: hardSticksAndRefusal.otherReason || '',
    };
    await updateCollectedCounts(stop.id, payload);
    dispatch(updateStopData({
      stopId: stop.id,
      updates: {
        collectedCounts: payload.collectedCounts,
        hardSticks: payload.hardSticks,
        refusals: payload.refusals,
        otherNotCollectedCount: payload.otherNotCollectedCount,
        otherReason: payload.otherReason,
      },
    }));
    setLocalCounts({});
    dispatch(showToast('Counts saved ✓'));
  } catch (err) {
    console.error('Count save failed', err);
    dispatch(showToast('Failed to save counts'));
  } finally {
    setSavingCounts(false);
  }
};

  useEffect(() => {
  if (!stop?.photos?.length) return;
  const seeded = {};
  stop.photos.forEach(p => {
    if (p.notes && p.fileKey) {
      seeded[p.fileKey] = { note: p.notes, fileKey: p.fileKey };
    }
  });
  setSpecimenNotes(seeded);
}, [stop?.id]);

  // ── Supply flags ──────────────────────────────────────────────────────────
  const handleFlag = async (field, val) => {
    const updated = { ...supplyFlags, [field]: val };
    setSupplyFlags(updated);

    const isSupplyIssue = field === 'supplies' && val === false;
    const isBackupIssue = field === 'backup' && val === true;

    if (!isSupplyIssue && !isBackupIssue) return;

    const payload = {
      stopId: stop.id,
      type: isSupplyIssue ? 'supplies_insufficient' : 'backup_needed',
      facilityId: stop.facilityId,
      desc: isSupplyIssue
        ? 'Supplies reported insufficient at this facility.'
        : 'Backup phlebotomist requested at this facility.',
    };

    setSavingFlags(true);
    try {
      await reportException(payload);
      dispatch(showToast('Supply alert sent ✓'));
    } catch {
      dispatch(showToast('Failed to send supply alert'));
    } finally {
      setSavingFlags(false);
    }
  };

  // ── Destination confirm (used for both s=2 and s=4 pickers) ──────────────
  /**
   * s=2 destination options:
   *   'leave'   → API 'ready'      → numeric 3  (courier picks up)
   *   'transit' → API 'in_transit' → numeric 4  (phleb carries specimens)
   *
   * s=4 (in_transit) destination options:
   *   'drop'    → API 'ready'      → numeric 3  (drop at another site — courier picks up from there)
   *   'lab'     → API 'delivered'  → numeric 6  (delivered directly to lab)
   */
  const handleConfirmDest = async () => {
    if (!canConfirmDest || advancing) return;

    const statusMap = {
      leave: 'ready',
      transit: 'in_transit',
      drop: 'ready',
      lab: 'delivered',
    };
    const apiStatus = statusMap[destSelected];
    if (!apiStatus) return;

    setAdvancing(true);
    try {

      // Drop-site facility id only applies to the s=4 "drop at another site" case
      if (destSelected === 'drop' && dropFacility) {
        await addDropSiteFacilityId(stop.id, dropFacility);
        await updateDriverUser(stop.id, pickupCourier)
      }

      // CLAUDE-SYNC: v1.7.20 — capture coordinates on EVERY status change
      // (arrived, in progress, in transit / "taking it with me", ready /
      // "leaving it here", picked up, delivered), not just arrived/delivered,
      // so the Clock-In Audit export can map the full route (per Tanaya).
      const coords = await getCoordsSafe();
      const result = await updateRouteStopStatus(stop.id, apiStatus, coords?.lat, coords?.lng);
      if (destSelected === 'leave' && (pickupCourier || pickupCourier !== '')) {
        await updateDriverUser(stop.id, pickupCourier);
      }

      if (destSelected === 'lab' && currentUser?.id) {
        await updateDriverUser(stop.id, currentUser?.id);
      }
      
      if (noteText.trim()) {
        await updatePhlebNotes(stop.id, noteText.trim());
      }
      const { timestamp, timeHHMM, date } = extractTimestamp(result);
      const newNumeric = STATUS_STRING_TO_NUMERIC[apiStatus];

      const dropFacilityName = destSelected === 'drop'
        ? (facilityData.find(f => f.id === dropFacility)?.name || '')
        : '';

      dispatch(advanceStatus({
        stopId: stop.id,
        newStatus: newNumeric,
        timestamp,
        timeMinutes: timeHHMM,
        specimenDestination: destSelected,
        // ...(destSelected === 'leave'  && pickupCourier && { courierId: pickupCourier }),
        ...(destSelected === 'leave' && noteText.trim() && { phlebNotes: noteText.trim() }),
        ...(destSelected === 'drop' && dropFacility && { dropOffFacilityId: dropFacility }),
      }));

      // For 'drop' — also add timeline event so ActivityTimeline shows the drop site
      if (destSelected === 'drop') {
        const dropFacilityName = facilityData.find(f => f.id === dropFacility)?.name || '';
        dispatch(updateStopData({
          stopId: stop.id,
          updates: {
            dropFacilityName,
            timeline: [
              ...(stop.timeline ?? []),
              {
                status: 'ready',
                time: timeHHMM,
                date,
                userId: stop.phlebId,
                note: dropFacilityName ? `Dropped at ${dropFacilityName}` : 'Dropped at site',
              },
            ],
          },
        }));
      }

      const toastMap = {
        leave: 'Ready for pickup ✓ — courier notified',
        transit: 'Marked In Transit ✓',
        drop: `Drop-off confirmed ✓${dropFacility ? ` — ${dropFacilityName}` : ''}`,
        lab: 'Delivered to lab ✓',
      };
      dispatch(showToast(toastMap[destSelected]));

      // Reset picker state
      setDestSelected('');
      setDestPhoto(false);
      setDropFacility('');
      setPickupCourier('');
      setNoteText('');
    } catch (err) {
      console.error('handleConfirmDest failed:', err);
      dispatch(showToast('Failed to update — please try again'));
    } finally {
      setAdvancing(false);
    }
  };

  // ── Confirm button label ──────────────────────────────────────────────────
  const confirmDestLabel =
    destSelected === 'leave' ? 'Confirm Ready for Pickup'
      : destSelected === 'transit' ? 'Mark In Transit'
        : destSelected === 'drop' ? 'Confirm Drop-Off'
          : destSelected === 'lab' ? 'Confirm Delivery to Lab'
            : 'Confirm';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {(lightboxUrl || lightboxImages) && (
        <Lightbox
          url={lightboxUrl}
          images={lightboxImages}
          startIndex={lightboxStartIndex}
          onClose={() => { setLightboxUrl(null); setLightboxImages(null); }}
        />
      )}

      <NoteModal
        open={noteModalOpen}
        // NoteModal existingNote — read by fileKey: 
        existingNote={selectedPhoto ? specimenNotes[selectedPhoto.fileKey]?.note : ''}
        fileKey={selectedPhoto?.fileKey}
        // In PhlebDetail, replace the onSave handler passed to NoteModal:

        onSave={async (text) => {
          if (!selectedPhoto) return;
          try {
            await uploadPhotoNotes(stop.id, {
              fileKey: selectedPhoto.fileKey,
              notes: text
            });
          } catch (err) {
            console.error('Note upload failed', err);
            dispatch(showToast('Failed to save note'));
            return;
          }

          // Update local state only after API succeeds
          setSpecimenNotes(prev => ({
            ...prev,
            [selectedPhoto.fileKey]: { note: text, fileKey: selectedPhoto.fileKey },
          }));

          dispatch(showToast('Note saved ✓'));
          setNoteModalOpen(false);
        }}
        onClose={() => setNoteModalOpen(false)}
      />

      <ExceptionPanel
        stopId={stop.id}
        facilityId={stop.facilityId}
        stopName={stop.facilityName}
        open={excOpen}
        onClose={() => setExcOpen(false)}
      />

      <CameraModal
        isOpen={cameraOpen}
        title={cameraTitle}
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />

      <div className="detail-panel open">
        <div className="detail-header">
          <button className="back-btn" onClick={() => dispatch(clearSelectedStop())}>← Back</button>
          <div>
            <div className="detail-title">{stop.facilityName}</div>
            <div className="detail-subtitle">{stop.addr} · Est. {utcTimeToLocal(stop.timeWindow)}</div>
          </div>
        </div>

        <div className="detail-body">

          {/* ── Priority alert ────────────────────────────────────────────── */}
          {stop.isPriority && (
            <div style={{
              background: '#FFF3E0', borderRadius: 12, padding: '10px 14px',
              marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
              borderLeft: '3px solid #FF6F00',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#E65100">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#E65100' }}>
                Stat — ensure all draws completed before leaving
              </div>
            </div>
          )}

          {/* ── Draw List Photo ───────────────────────────────────────────── */}
          {/* CLAUDE-SYNC: v1.4 S1 — multiple draw list photos allowed (2026-06-23) */}
          <div className="card">
            <div className="card-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Draw List Photo
            </div>
            <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 8 }}>
              Snap the facility's draw list before starting your draws. Tap + to add more pages.
            </div>
            <div className="photo-strip">
              {/* All previously captured draw list photos */}
              {photos
                .filter((p) => p.photoType === 'draw_list' || p.label === 'Draw List')
                .map((p) => (
                  <div
                    key={p.id}
                    className="photo-thumb taken"
                    style={{ position: 'relative', overflow: 'hidden', cursor: resolveSrc(p) ? 'pointer' : 'default' }}
                    onClick={() => { if (resolveSrc(p)) openLightbox(p); }}
                  >
                    {resolveSrc(p) ? (
                      <>
                        <img src={resolveSrc(p)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} alt="Draw list" />
                      </>
                    ) : (
                      <><div style={{ fontSize: 20 }}>✅</div><div>Saved</div></>
                    )}
                  </div>
                ))}
              {/* + button — always visible so phleb can add more pages */}
              <div
                className={`photo-thumb ${uploadingPhoto && cameraType === 'draw_list' ? 'taken' : ''}`}
                style={{ width: 72, height: 72, cursor: uploadingPhoto ? 'not-allowed' : 'pointer' }}
                onClick={() => !uploadingPhoto && openCamera('draw_list', 'Draw List Photo')}
              >
                {uploadingPhoto && cameraType === 'draw_list' ? (
                  <><div style={{ fontSize: 14 }}>⏳</div><div>Saving</div></>
                ) : (
                  <><div style={{ fontSize: 20, color: 'var(--berry)' }}>📷</div><div>{dlCaptured ? 'Add' : 'Draw List'}</div></>
                )}
              </div>
            </div>
            {dlCaptured && (
              <div style={{ fontSize: 11, color: '#1a7a40', marginTop: 6 }}>
                {photos.filter(p => p.photoType === 'draw_list' || p.label === 'Draw List').length} page{photos.filter(p => p.photoType === 'draw_list' || p.label === 'Draw List').length !== 1 ? 's' : ''} captured ✓ — tap + to add more
              </div>
            )}
          </div>

          {/* ── Status Progress ───────────────────────────────────────────── */}
          <div className="card">
            <div className="card-label">Status Progress</div>
            {/*
              StatusPipeline receives:
                isInTransit=true  → shows in_transit step in pipeline (STATUS_PIPELINE_TRANSIT)
                isInTransit=false → shows standard pipeline (STATUS_PIPELINE: ready → picked_up → delivered)
            */}
            <StatusPipeline
              status={stop.timeline || []}
              isInTransit={isInTransit || (!isPhlebOnly && (stop.timeline ?? []).some(e => e.status === 'in_transit'))}
              isDropPath={isDropPath}
            />
            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <StatusBadge status={s} />
            </div>
          </div>

          {/* ── Facility Supplies (s=1 only) ──────────────────────────────── */}
          {s === 1 && (
            <div className="card">
              <div className="card-label">Facility Supplies</div>
              <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 12 }}>
                Flag any supply issues so Jason and Jasmine are notified.
              </div>

              <div style={{ background: '#F7F4F8', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Supplies sufficient?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map((val) => {
                    const isSelected = supplyFlags.supplies === val;
                    const label = val ? 'Yes' : 'No';
                    const selectedColor = val ? 'var(--rp)' : '#c62828';
                    return (
                      <button
                        key={label}
                        onClick={() => handleFlag('supplies', val)}
                        style={{
                          flex: 1, padding: 8, borderRadius: 8, cursor: 'pointer',
                          fontWeight: 600, fontSize: 12, fontFamily: "'Poppins', sans-serif",
                          border: `1.5px solid ${isSelected ? selectedColor : 'var(--rgray)'}`,
                          background: isSelected ? selectedColor : 'white',
                          color: isSelected ? 'white' : 'var(--bodygray)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: '#F7F4F8', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Backup needed?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[false, true].map((val) => {
                    const isSelected = supplyFlags.backup === val;
                    const label = val ? 'Yes' : 'No';
                    const selectedColor = val ? '#c62828' : 'var(--rp)';
                    return (
                      <button
                        key={label}
                        onClick={() => handleFlag('backup', val)}
                        style={{
                          flex: 1, padding: 8, borderRadius: 8, cursor: 'pointer',
                          fontWeight: 600, fontSize: 12, fontFamily: "'Poppins', sans-serif",
                          border: `1.5px solid ${isSelected ? selectedColor : 'var(--rgray)'}`,
                          background: isSelected ? selectedColor : 'white',
                          color: isSelected ? 'white' : 'var(--bodygray)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Specimen Counts (s=2 or s=3, not in_transit=4 or delivered=6) */}
          {s >= 2 && s !== 4 && s !== 6 && expectedCounts.length > 0 && (
            <div className="card">
              <div className="card-label">Collected Specimen Counts</div>
              <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 12 }}>
                Enter actual counts collected at this facility. Courier will verify at pickup.
              </div>

              {expectedCounts.map(({ id, count: expected }) => {
                const sid = String(id);
                const name = specimenLabelMap[sid] || specimenLabelMap[id] ||
                  (sid.length < 30 ? sid.charAt(0).toUpperCase() + sid.slice(1) : 'Specimen');
                const collected = Number(displayCounts[sid] ?? 0);

                return (
                  <div
                    key={sid}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      marginBottom: 10, padding: '10px 5px',
                      borderBottom: '1px solid var(--rgray)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--rp)' }}>{name}</div>
                      <div style={{ fontSize: 11, color: 'var(--bodygray)', marginTop: 2, fontWeight: 400 }}>
                        {collected} / {expected} collected
                      </div>
                    </div>
                    <Stepper
                      currentStage={s}
                      value={collected}
                      max={Infinity}
                      onChange={(val) => setLocalCounts((prev) => ({ ...prev, [sid]: val }))}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Hard Sticks & Refusals (s=2 or s=3, not in_transit/delivered) */}
          {s >= 2 && s !== 4 && s !== 6 && (
            <div style={{ margin: '12px 0', padding: '10px 12px', background: '#FEF3E2', borderRadius: 12, border: '1.5px solid #f59e0b' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b36a00', marginBottom: 6 }}>
                Hard Sticks & Refusals
              </div>
              {/* CLAUDE-SYNC: v1.7.20 — "Track daily — these are NOT exceptions." subtitle
                  removed (Frank, Oct 6). Already applied on staging by Virsoftech;
                  this brings GitHub in line with staging. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--rp)', marginBottom: 4 }}>Hard Sticks</div>
                <Stepper 
                  currentStage={s}
                  value={hardSticksCounts}
                  max={Infinity}
                  onChange={(val) => setHardSticksAndRefusal((p) => ({ ...p, hardSticks: val }))}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--rp)', marginBottom: 4 }}>Refusals</div>
                <Stepper
                  currentStage={s}
                  value={refusalsCounts}
                  max={Infinity}
                  onChange={(val) => setHardSticksAndRefusal((p) => ({ ...p, refusals: val }))}
                />
              </div>
              {/* CLAUDE-SYNC: v1.7.12 — "Other" non-collection count (patient
                  showering, eating, deceased, etc.), same stepper pattern as
                  Hard Sticks/Refusals. Per Tanaya's Aug 26 call: this needs
                  to be a real number so it's accounted for in the total,
                  separate from the free-text note below. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--rp)', marginBottom: 4 }}>Other</div>
                <Stepper
                  currentStage={s}
                  value={otherNotCollectedCounts}
                  max={Infinity}
                  onChange={(val) => setHardSticksAndRefusal((p) => ({ ...p, otherNotCollectedCount: val }))}
                />
              </div>
              <div style={{
                marginTop: 12, padding: '8px 14px', borderRadius: 20,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 600,
                background: 'rgba(179,106,0,0.10)', color: '#b36a00', border: '1px solid #f59e0b',
              }}>
                {`${totalCollectedCountSum} drawn + ${hardSticksCounts} hard sticks + ${refusalsCounts} refusals + ${otherNotCollectedCounts} other = ${totalAccountedFor} of ${totalExpectedCountSum} accounted for`}
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--rp)', marginBottom: 4 }}>
                  Notes <span style={{ fontSize: 11, fontWeight: 400, color: '#b36a00' }}>(optional — not sent to the client)</span>
                </div>
                <textarea
                  rows={2}
                  placeholder="e.g. patient in procedure, no order found..."
                  value={hardSticksAndRefusal.otherReason}
                  onChange={(e) => setHardSticksAndRefusal((p) => ({ ...p, otherReason: e.target.value }))}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: 10,
                    border: '1px solid #f59e0b', fontFamily: 'inherit',
                    fontSize: 12, color: '#1a1a1a', resize: 'none', outline: 'none',
                    background: 'rgba(255,255,255,0.8)',
                  }}
                />
              </div>
            </div>
          )}

          {/* Save Counts button — only while counts are editable (s < 3) */}
          {s < 3 && hasUnsavedChanges && (
            <button
              className="action-btn primary"
              style={{ marginTop: 6 }}
              onClick={handleSaveCounts}
              disabled={savingCounts}
            >
              {savingCounts ? 'Saving…' : 'Save Counts'}
            </button>
          )}
          <div className="card">
            <div className="card-label">Next Action</div>

            {(s !== 3 && s !== 6) && (() => {
              const specimenPhotos = photos.filter(p =>
                p.photoType === 'specimen' || p.label === 'Specimens Photo'
              );
              const otherPhotos = photos.filter(p =>
                p.photoType !== 'draw_list' &&
                p.label !== 'Draw List' &&
                p.photoType !== 'specimen' &&
                p.label !== 'Specimens Photo' &&
                !(s === 4 && (p.photoType === 'handoff' || p.photoType === 'delivery'))
              );
              const isSpecimenStep = s === 2 && !isPhlebOnly;

              return (
                <div style={{ marginBottom: 12 }}>
                  {isSpecimenStep ? (
                    <>
                      <div style={{ background: '#EDE4F0', borderRadius: 10, padding: '8px 12px', marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--rp)' }}>📷 Specimen photos required</div>
                        <div style={{ fontSize: 10, color: 'var(--bodygray)', marginTop: 2 }}>
                          Take at least one. Add more with +. Note the refrigerator location for each.
                        </div>
                      </div>
                      {/* Existing specimen photos with note fields */}
                      {specimenPhotos.map((p, i) => (
                        <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5 }}>
                          <div
                            className="photo-thumb taken"
                            style={{ flexShrink: 0, cursor: resolveSrc(p) ? 'pointer' : 'default' }}
                            onClick={() => resolveSrc(p) && openLightbox(p)}
                          >
                            {resolveSrc(p) ? (
                              <img src={resolveSrc(p)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: "10px" }} alt={`Specimen ${i + 1}`} />
                            ) : (
                              <><div style={{ fontSize: 16 }}>✅</div><div>Saved</div></>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>

                            {(() => {
                              const currentNote = specimenNotes[p.fileKey]?.note || '';
                              return (
                                <div style={{ marginTop: 12 }}>
                                  {currentNote ? (
                                    <div style={{
                                      background: '#F7F4F8',
                                      borderRadius: 10,
                                      padding: '4px 12px',
                                    }}>
                                      <div style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>{currentNote}</div>
                                    </div>
                                  ) : null}
                                  <button
                                    onClick={() => {
                                      setSelectedPhoto(p);
                                      setNoteModalOpen(true);
                                    }}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 7,
                                      padding: '9px 14px', borderRadius: 10,
                                      border: 'none',
                                      background: 'white',
                                      color: 'var(--rp)',
                                      fontSize: 12, fontWeight: 600,
                                      cursor: 'pointer', fontFamily: 'inherit',
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    {currentNote ? 'Edit Note' : 'Add Note'}
                                  </button>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                      {/* + Add another photo */}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                        <div
                          className={`photo-thumb ${uploadingPhoto && cameraType === 'specimen' ? 'taken' : ''}`}
                          style={{ flexShrink: 0, cursor: uploadingPhoto ? 'not-allowed' : 'pointer' }}
                          onClick={() => !uploadingPhoto && openCamera('specimen', 'Specimen Photo')}
                        >
                          {uploadingPhoto && cameraType === 'specimen' ? (
                            <><div style={{ fontSize: 14 }}>⏳</div><div>Saving</div></>
                          ) : (
                            <><div style={{ fontSize: 20, color: 'var(--berry)' }}></div>📷<div>Add</div></>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: specimenPhotos.length === 0 ? '#c62828' : 'var(--bodygray)' }}>
                          {specimenPhotos.length === 0
                            ? 'At least 1 specimen photo required'
                            : `${specimenPhotos.length} photo${specimenPhotos.length > 1 ? 's' : ''} — tap + to add more`}
                        </div>
                      </div>
                    </>
                  ) : (
                    // Non-specimen steps: standard single photo strip
                    s < 3 && (
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 8 }}>{photoStepLabel}</div>
                        <div className="photo-strip">
                          {otherPhotos.map((p) => (
                            <div
                              key={p.id}
                              className="photo-thumb taken"
                              style={{ position: 'relative', overflow: 'hidden', cursor: resolveSrc(p) ? 'pointer' : 'default' }}
                              onClick={() => openLightbox(p)}
                            >
                              {resolveSrc(p) ? (
                                <>
                                  <img src={resolveSrc(p)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={p.label} />
                                </>
                              ) : (
                                <><div style={{ fontSize: 16 }}>✅</div><div>Saved</div></>
                              )}
                            </div>
                          ))}
                          <div
                            className={`photo-thumb ${uploadingPhoto ? 'taken' : ''}`}
                            style={{ width: 72, height: 72, cursor: uploadingPhoto ? 'not-allowed' : 'pointer' }}
                            onClick={() => !uploadingPhoto && openCamera(PHOTO_STEP_TYPE[s] ?? 'specimen', photoStepLabel)}
                          >
                            {uploadingPhoto && cameraType === (PHOTO_STEP_TYPE[s] ?? 'specimen') ? (
                              <><div style={{ fontSize: 14 }}>⏳</div><div>Saving</div></>
                            ) : (
                              <><div style={{ fontSize: 20, color: 'var(--berry)' }}>📷</div><div>Add</div></>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              );
            })()}

            {/* ── s=0 or s=1: Simple advance button ───────────────────────── */}
            {nextActionLabel && (
              <button
                className="action-btn primary"
                onClick={handleAdvance}
                disabled={advancing}
              >
                {advancing ? 'Updating…' : nextActionLabel}
              </button>
            )}

            {/* ── s=2: "Where are the specimens going?" picker ─────────────── */}
            {/* CLAUDE-SYNC: v1.4 S2 — specimen photo required before destination picker shows (2026-06-23) */}
            {s === 2 && !isPhlebOnly && (() => {
              const specimenPhotos = photos.filter(p => p.photoType === 'specimen' || p.label === 'Specimens Photo');
              if (specimenPhotos.length === 0) {
                return (
                  <div style={{ background: '#FFEBEE', borderRadius: 10, padding: '10px 14px', textAlign: 'center', marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#c62828' }}>
                      📷 Add at least one specimen photo above to continue
                    </div>
                  </div>
                );
              }
              return (
                <DestinationPickerLeaveOrTransit
                  destSelected={destSelected}
                  setDestSelected={(v) => {
                    setDestSelected(v);
                    setDropFacility('');
                    setPickupCourier(v === 'leave' ? (facilityDefaultDriverId || '') : '');
                    setNoteText('');
                  }}
                  pickupCourier={pickupCourier}
                  setPickupCourier={setPickupCourier}
                  couriers={couriers}
                  loadingCouriers={loadingCouriers}
                  courierError={courierError}
                  facilityDefaultDriverId={facilityDefaultDriverId}
                  note={note}
                  noteText={noteText}
                  setNoteText={setNoteText}
                  savingNote={savingNote}
                  handleNoteBlur={handleNoteBlur}
                  advancing={advancing}
                  onConfirm={handleConfirmDest}
                  confirmDestLabel={confirmDestLabel}
                  destSelected_canConfirm={canConfirmDest}
                  onOpenNoteModal={() => setNoteModalOpen(true)}
                  savedNote={savedNote}
                  currentUserId={currentUser?.id}
                />
              );
            })()}

            {/* ── s=4 (in_transit): "Where are you delivering?" picker ──────── */}
            {s === 4 && !isPhlebOnly && (
              <DestinationPickerDropOrLab
                destSelected={destSelected}
                setDestSelected={(v) => { setDestSelected(v); setDropFacility(''); setDestPhoto(false); }}
                dropFacility={dropFacility}
                setDropFacility={setDropFacility}
                facilityData={facilityData}
                destPhoto={destPhoto}
                advancing={advancing}
                uploadingPhoto={uploadingPhoto}
                onConfirm={handleConfirmDest}
                confirmDestLabel={confirmDestLabel}
                canConfirm={canConfirmDest}
                openCamera={openCamera}
                photos={photos}
                openLightbox={openLightbox}
                pickupCourier={pickupCourier}
                couriers={couriers}
                setCouriers={setCouriers}
                setPickupCourier={setPickupCourier}
              />
            )}

            {/* ── s=3 (ready for pickup): All done at this stop ────────────── */}
            {s === 3 && (
              <div style={{
                background: '#E8F8EF', borderRadius: 12, padding: 16,
                textAlign: 'center', border: '1px solid #1a7a40',
              }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1a7a40' }}>
                  ✅ All done at this stop
                </div>
                <div style={{ fontSize: 12, marginTop: 6, color: '#666' }}>
                  {(stop.dropFacilityName || stop.dropFacilityId)
                    ? 'Specimens dropped off. Ready for courier pickup.'
                    : 'Specimens ready for pickup — courier has been notified.'}
                </div>
                {(stop.dropFacilityName || stop.dropFacilityId) && (
                  <div style={{ fontSize: 12, marginTop: 4, color: '#1a7a40', fontWeight: 600 }}>
                    Dropped at: {stop.dropFacilityName || stop.dropFacilityId}
                  </div>
                )}
              </div>
            )}

            {/* ── s=6 (delivered): Final delivered state ────────────────────── */}
            {s === 6 && (
              <div style={{
                background: '#E8F8EF', borderRadius: 12, padding: 16,
                textAlign: 'center', border: '1px solid #1a7a40',
              }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1a7a40' }}>
                  ✅ Delivered to lab
                </div>
                <div style={{ fontSize: 12, marginTop: 6, color: '#666' }}>
                  Specimens confirmed delivered to Rhea Lab, Melrose Park.
                </div>
              </div>
            )}
          </div>

          {/* ── Exception ─────────────────────────────────────────────────── */}
          <button className="action-btn danger" onClick={() => setExcOpen(true)}>
            Report Exception
          </button>

          {/* ── View All Photos ───────────────────────────────────────────── */}
          {/* CLAUDE-SYNC: v1.4 S8 — phleb can review all submitted photos (2026-06-23) */}
          {photos.length > 0 && (
            <button
              className="view-photos-btn"
              style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'white', border: '1px solid var(--rgray)', borderRadius: 14, padding: '12px 16px', cursor: 'pointer' }}
              onClick={() => {
                const allPhotoUrls = photos.map(resolveSrc).filter(Boolean);
                if (allPhotoUrls.length > 0) {
                  setLightboxImages(allPhotoUrls);
                  setLightboxStartIndex(0);
                }
              }}
            >
              <div style={{ fontSize: 22, color: 'var(--berry)' }}>📷</div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--rp)' }}>View All Photos</div>
                <div style={{ fontSize: 11, color: 'var(--bodygray)', marginTop: 2 }}>
                  Draw list · Arrival · Specimen · {photos.length} total
                </div>
              </div>
              <div style={{ background: 'var(--berry)', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                {photos.length}
              </div>
            </button>
          )}

          {/* ── Activity Timeline ─────────────────────────────────────────── */}
          <div className="card">
            <div className="card-label">Activity Timeline</div>
            <ActivityTimeline
              timelines={stop.timeline || []}
              phlebName={stop.phlebName}
              driverName={stop.driverName}
              isPhlebOnly={isPhlebOnly}
              phlebId={stop.phlebId}
            />
          </div>

          {/* ── Facility Info ─────────────────────────────────────────────── */}
          <div className="card">
            <div className="card-label">Facility Info</div>
            <div style={{ fontSize: 13, color: 'var(--bodygray)', lineHeight: 2 }}>
              <div>📞 {stop.phone || '—'}</div>
              <div>👤 {stop.contact || '—'}</div>
              {stop.notes && <div style={{ color: '#b36a00' }}>✎ {stop.notes}</div>}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

function DestinationPickerLeaveOrTransit({
  destSelected,
  setDestSelected,
  pickupCourier,
  setPickupCourier,
  couriers,
  loadingCouriers,
  courierError,
  facilityDefaultDriverId,
  note,
  noteText,
  setNoteText,
  savingNote,
  handleNoteBlur,
  advancing,
  onConfirm,
  confirmDestLabel,
  destSelected_canConfirm,
  onOpenNoteModal,
  savedNote,
  currentUserId
}) {
  const opts = [
    {
      id: 'leave',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      ),
      title: 'Leaving here — ready for courier pickup',
      sub: 'Specimens stay at this facility. Courier collects them.',
      color: '#1a7a40', bg: '#E8F8EF',
    },
    {
      id: 'transit',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="1" y="3" width="15" height="13" rx="2" />
          <path d="M16 8h4l3 5v3h-7V8z" />
          <circle cx="5.5" cy="18.5" r="2.5" />
          <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
      ),
      title: 'Taking with me — in transit',
      sub: 'I am carrying specimens to the next stop or lab.',
      color: '#1A7A7A', bg: '#E0F7F4',
    },
  ];

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>
        Where are the specimens going?
      </div>
      <div style={{ fontSize: 12, color: '#8A7A8D', marginBottom: 14 }}>
        Select one. Courier info required when leaving specimens here.
      </div>

      {opts.map((opt) => (
        <button
          key={opt.id}
          onClick={() => setDestSelected(opt.id)}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
            padding: '12px 14px', borderRadius: 12, marginBottom: 8,
            textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
            border: destSelected === opt.id ? `1.5px solid ${opt.color}` : '1.5px solid #E8E4EA',
            background: destSelected === opt.id ? opt.bg : 'white',
            transition: 'all 0.15s',
          }}
        >
          <span style={{ color: destSelected === opt.id ? opt.color : '#8A7A8D', flexShrink: 0, marginTop: 1 }}>{opt.icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 2 }}>{opt.title}</div>
            <div style={{ fontSize: 11, color: '#8A7A8D' }}>{opt.sub}</div>
          </div>
        </button>
      ))}

      {/* Leave sub-section */}
      {destSelected === 'leave' && (
        <div style={{ background: '#E8F8EF', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#1a7a40', marginBottom: 6 }}>
            Who is picking these up?
            {!facilityDefaultDriverId && <span style={{ color: '#c62828' }}> (required)</span>}
          </div>
          <select
            value={pickupCourier || facilityDefaultDriverId || ''}
            onChange={(e) => setPickupCourier(e.target.value)}
            disabled={loadingCouriers}
            style={{
              width: '100%', padding: '8px 10px',
              border: '1px solid #E8E4EA', borderRadius: 8,
              fontFamily: 'inherit', fontSize: 13, outline: 'none', background: 'white',
            }}
          >
            {pickupCourier === '' && <option value={""}>Choose a courier or phlebotomist</option>}
            {/* CLAUDE-SYNC: v1.6 N2 — couriers + phlebs + TBD phleb (2026-07-17) */}
            {couriers.filter(c => !c._isPhlebPickup).length > 0 && (
                <optgroup label="── Couriers ──">
                  {couriers.filter(c => c._isTbd).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                  {couriers.filter(c => !c._isPhlebPickup && !c._isTbd).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </optgroup>
              )}
            <optgroup label="── Phlebotomists ──">
                {couriers
                  .filter(c => c._isPhlebPickup && c.id !== currentUserId)   // <-- filter out current phleb
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </optgroup>
          </select>
          {courierError && (
            <div style={{ fontSize: 10, color: '#c62828', marginTop: 6 }}>
              Couldn't load couriers — you can still leave this as TBD.
            </div>
          )}
           <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--bodygray)', marginBottom: 6 }}>
              Note for courier (optional)
            </div>
            <textarea
              className="note-input"
              placeholder="e.g. samples left at front desk..."
              defaultValue={note}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={handleNoteBlur}
            />
            {savingNote && <div style={{ fontSize: 11, color: 'var(--bodygray)', marginTop: 4 }}>Saving note…</div>}
          </div>
        </div>
      )}

      <button
        disabled={!destSelected || advancing}
        onClick={onConfirm}
        style={{
          width: '100%', padding: '12px', borderRadius: 10, border: 'none',
          background: destSelected ? 'var(--rp)' : '#ccc',
          color: destSelected ? 'white' : '#999',
          fontSize: 14, fontWeight: 600,
          cursor: destSelected ? 'pointer' : 'not-allowed',
          fontFamily: "'Poppins', sans-serif",
          transition: 'all 0.15s',
        }}
      >
        {advancing ? 'Updating…' : confirmDestLabel}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: s=4 (in_transit) picker — Drop at site OR Deliver to lab
// ─────────────────────────────────────────────────────────────────────────────
function DestinationPickerDropOrLab({
  destSelected,
  setDestSelected,
  pickupCourier,
  couriers,
  setCouriers,
  setPickupCourier,
  dropFacility, setDropFacility, facilityData,
  destPhoto,
  advancing, onConfirm, confirmDestLabel, canConfirm,
  uploadingPhoto, openCamera,
  photos = [], openLightbox,
}) {
  const opts = [
    {
      id: 'drop',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
      ),
      title: 'Dropping at another site',
      sub: 'Leaving specimens at a different facility for pickup.',
      color: '#b36a00', bg: '#FEF3E2',
    },
    {
      id: 'lab',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      title: 'Delivering directly to lab',
      sub: 'You are going straight to Rhea Lab, Melrose Park now.',
      color: '#1a7a40', bg: '#E8F8EF',
    },
  ];

  // Camera type/title depend on which destination is selected:
  //   drop → 'handoff'  (PhotoType.handoff)
  //   lab  → 'delivery' (PhotoType.delivery)
  const photoCameraType = destSelected === 'lab' ? 'delivery' : 'handoff';
  const photoCameraTitle = destSelected === 'lab' ? 'Delivery Photo' : 'Handoff Photo';
  const relevantPhotos = photos.filter(p => String(p.photoType) === photoCameraType);
  const hasTransitPhotos = relevantPhotos.length > 0 || destPhoto;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>
        Where are you delivering the specimens?
      </div>
      <div style={{ fontSize: 12, color: '#8A7A8D', marginBottom: 14 }}>
        Select one. A handoff photo is optional for drop-off or lab delivery.
      </div>

      {opts.map((opt) => (
        <button
          key={opt.id}
          onClick={() => setDestSelected(opt.id)}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
            padding: '12px 14px', borderRadius: 12, marginBottom: 8,
            textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
            border: destSelected === opt.id ? `1.5px solid ${opt.color}` : '1.5px solid #E8E4EA',
            background: destSelected === opt.id ? opt.bg : 'white',
            transition: 'all 0.15s',
          }}
        >
          <span style={{ color: destSelected === opt.id ? opt.color : '#8A7A8D', flexShrink: 0, marginTop: 1 }}>{opt.icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 2 }}>{opt.title}</div>
            <div style={{ fontSize: 11, color: '#8A7A8D' }}>{opt.sub}</div>
          </div>
        </button>
      ))}

      {/* Drop sub-section — facility selector */}
      {destSelected === 'drop' && (
        <div style={{ background: '#FEF3E2', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#b36a00', marginBottom: 6 }}>
            Which facility? <span style={{ color: '#c62828' }}>(required)</span>
          </div>
          <select
            value={dropFacility}
            onChange={(e) => setDropFacility(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px',
              border: '1px solid #E8E4EA', borderRadius: 8,
              fontFamily: 'inherit', fontSize: 13, outline: 'none', background: 'white',
            }}
          >
            <option value="">Select a facility…</option>
            {facilityData.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      {destSelected === 'drop' && (
        <div style={{ background: '#FEF3E2', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#b36a00', marginBottom: 6 }}>
            Select a driver to assign ? <span style={{ color: '#c62828' }}>(required)</span>
          </div>
          <select
            value={pickupCourier}
            onChange={(e) => setPickupCourier(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px',
              border: '1px solid #E8E4EA', borderRadius: 8,
              fontFamily: 'inherit', fontSize: 13, outline: 'none', background: 'white',
            }}
          >
            <option value="">Choose a Driver..</option>
            {couriers.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Lab sub-section — confirmation info */}
      {destSelected === 'lab' && (
        <div style={{ background: '#E8F8EF', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#1a7a40' }}>Delivering to Rhea Lab, Melrose Park</div>
          <div style={{ fontSize: 10, color: '#8A7A8D', marginTop: 3 }}>
            Stop will be marked <strong>Delivered</strong> on confirmation.
          </div>
        </div>
      )}

      {/* Photo — optional for both drop and lab */}
      {(destSelected === 'drop' || destSelected === 'lab') && (
        <div style={{ marginTop: 8, marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#8A7A8D', marginBottom: 8 }}>
            {destSelected === 'lab' ? 'Delivery photo' : 'Drop-off / handoff photo'}
            {' '}<span style={{ color: '#8A7A8D' }}>(optional)</span>
          </div>
          <div
            onClick={() => !uploadingPhoto && openCamera(photoCameraType, photoCameraTitle)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              border: `2px dashed ${hasTransitPhotos ? '#27AE60' : 'var(--berry)'}`,
              borderRadius: 10, cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
              background: hasTransitPhotos ? '#F0FFF4' : '#fdf8ff',
              marginBottom: relevantPhotos.length > 0 ? 8 : 0,
            }}
          >
            <span style={{ fontSize: 22 }}>{uploadingPhoto ? '⏳' : hasTransitPhotos ? '✅' : '📷'}</span>
            <span style={{ fontSize: 12, color: hasTransitPhotos ? '#1a7a40' : 'var(--berry)', fontWeight: 600 }}>
              {uploadingPhoto ? 'Uploading…' : hasTransitPhotos ? `${photoCameraTitle} captured ✓ — tap for more` : 'Tap to take a photo (optional)'}
            </span>
          </div>
          {relevantPhotos.length > 0 && (
            <div className="photo-strip">
              {relevantPhotos.map(p => {
                const src = p?.previewUrl || p?.url || (p?.fileKey ? `/api${p.fileKey}` : null);
                return (
                  <div
                    key={p.fileKey }
                    className="photo-thumb taken"
                    style={{ position: 'relative', overflow: 'hidden', cursor: src ? 'pointer' : 'default' }}
                    onClick={() => src && openLightbox && openLightbox(p)}
                  >
                    {src ? (
                      <>
                        <img src={src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={p.label || photoCameraTitle} />
                      </>
                    ) : (
                      <><div style={{ fontSize: 16 }}>✅</div><div>Saved</div></>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <button
        disabled={!canConfirm || advancing}
        onClick={onConfirm}
        style={{
          width: '100%', padding: '12px', borderRadius: 10, border: 'none',
          background: canConfirm ? 'var(--rp)' : '#ccc',
          color: canConfirm ? 'white' : '#999',
          fontSize: 14, fontWeight: 600,
          cursor: canConfirm ? 'pointer' : 'not-allowed',
          fontFamily: "'Poppins', sans-serif",
          transition: 'all 0.15s',
        }}
      >
        {advancing ? 'Updating…' : confirmDestLabel}
      </button>
    </div>
  );
}

function PhlebDetail() {
  const dispatch = useDispatch();
  const handleBack = () => dispatch(clearSelectedStop());
  return (
    <ErrorBoundaryDetail onBack={handleBack}>
      <PhlebDetailInner />
    </ErrorBoundaryDetail>
  );
}

export default PhlebDetail;